import { motion } from "framer-motion";
import { TiltCard } from "@/components/ui/tilt-card";


const timeline = [
  {
    year: "2020",
    title: "The Beginning",
    desc: "Founded Mononites Food Services in Chennai as a small-scale operation with a vision to deliver premium, home-style South Indian meals to offices and workplaces.",
  },
  {
    year: "2022",
    title: "Scaling Up",
    desc: "Expanded operations to multiple IT firms and MNCs across Chennai. Achieved FSSAI certification, launched customized meal plans, and built a dedicated cloud kitchen.",
  },
  {
    year: "Now",
    title: "Where We Are Today",
    desc: "Serving 5000+ meals, cafeteria management, and sustainable practices driving our next phase of growth.",
    current: true,
  },
];

const services = [
  {
    icon: "🍱",
    title: "Workplace Catering",
    desc: "Daily meal plans for IT firms and MNCs with customizable menus based on dietary preferences and on-time delivery.",
  },
  {
    icon: "🎉",
    title: "Event Catering",
    desc: "Weddings, birthdays, conferences and office events with flexible, large-scale menu options tailored to your occasion.",
  },
  {
    icon: "📦",
    title: "Bulk Orders & Meal Plans",
    desc: "Healthy meal plans for office cafeterias, seasonal festive menus, and large-scale food supply for institutions.",
  },
  {
    icon: "🏢",
    title: "Cafeteria Management",
    desc: "Full cafeteria operations for offices — from kitchen setup and staffing to daily menu planning and quality checks.",
  },
];

export function About() {
  return (
    <section
      id="about"
      className="relative py-20 sm:py-28 md:py-32"
    >
      <div className="max-w-7xl mx-auto px-5 sm:px-6 lg:px-10 grid lg:grid-cols-2 gap-12 lg:gap-16">
        {/* Left — About Mononites */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7 }}
        >
          <div className="flex items-center gap-3 mb-5 sm:mb-6">
            <span className="h-px w-12 bg-gold" />
            <span className="text-xs uppercase tracking-[0.4em] text-gold">About Mononites & Our Journey</span>
          </div>
          <h2 className="font-display text-3xl sm:text-4xl md:text-5xl leading-tight">
            A quieter kind of{" "}
            <span className="gradient-gold-text italic">excellence.</span>
          </h2>
          <p className="mt-5 sm:mt-6 text-muted-foreground text-base sm:text-lg leading-relaxed font-light">
            At Mononites Food Services, we bridge the gap between tradition and the modern workplace. We recognize that food is the fuel for India’s workforce, which is why we treat every plate with the sanctity of a home-cooked meal.
          </p>
          <p className="mt-4 text-muted-foreground leading-relaxed font-light text-sm sm:text-base">
            Our service model pairs the time-honored flavors of South Indian vegetarian cuisine with a "zero-error" mindset. From strict hygiene protocols to consistent taste profiles, we deliver food services that are as reliable as they are authentic.
          </p>

          <div className="mt-6 sm:mt-8 flex items-center gap-3">
            <a
              href="#contact"
              className="group inline-flex items-center gap-2 text-xs sm:text-sm uppercase tracking-[0.2em] text-gold hover:text-gold/80 transition-colors"
            >
              Start a conversation
              <span className="transition-transform group-hover:translate-x-1">→</span>
            </a>
          </div>
        </motion.div>

        {/* Right — Timeline */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7, delay: 0.15 }}
        >

          <div className="relative">
            {/* Vertical timeline line */}
            <div className="absolute left-[7px] top-2 bottom-2 w-px bg-gradient-to-b from-gold/60 via-gold/30 to-transparent" />

            <div className="space-y-6 sm:space-y-7">
              {timeline.map((item, i) => (
                <motion.div
                  key={item.year}
                  initial={{ opacity: 0, x: -16 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.45, delay: i * 0.08 }}
                  className="relative flex gap-4 sm:gap-5"
                >
                  {/* Dot */}
                  <div className="relative z-10 flex-shrink-0 mt-1.5">
                    {"current" in item && item.current ? (
                      <span className="relative flex h-[15px] w-[15px]">
                        <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75" />
                        <span className="relative inline-flex rounded-full h-[15px] w-[15px] bg-emerald-500 ring-2 ring-emerald-500/30" />
                      </span>
                    ) : (
                      <span className="block h-[15px] w-[15px] rounded-full border-2 border-gold bg-[var(--navy-deep)]" />
                    )}
                  </div>

                  {/* Content */}
                  <div className="pb-1">
                    <span
                      className={`inline-block px-2.5 py-0.5 rounded-full text-[10px] font-medium uppercase tracking-widest mb-1.5 ${
                        "current" in item && item.current
                          ? "bg-emerald-500/15 text-emerald-400"
                          : "bg-gold/10 text-gold"
                      }`}
                    >
                      {item.year}
                    </span>
                    <h4 className="font-display text-base sm:text-lg text-foreground">{item.title}</h4>
                    <p className="mt-1 text-xs sm:text-sm text-muted-foreground leading-relaxed font-light">
                      {item.desc}
                    </p>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </motion.div>
      </div>

      {/* Services sub-section */}
      <div id="services" className="max-w-7xl mx-auto px-5 sm:px-6 lg:px-10 mt-16 sm:mt-20 scroll-mt-24">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-30px" }}
          transition={{ duration: 0.65 }}
          className="mb-10 sm:mb-12 text-center"
        >
          <div className="flex items-center justify-center gap-3 mb-5">
            <span className="h-px w-12 bg-gold" />
            <span className="text-xs uppercase tracking-[0.4em] text-gold">Our Services</span>
            <span className="h-px w-12 bg-gold" />
          </div>
          <h3 className="font-display text-2xl sm:text-3xl md:text-4xl text-foreground">
            What we <span className="gradient-gold-text italic">deliver.</span>
          </h3>
        </motion.div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-5">
          {services.map((s, i) => (
            <motion.div
              key={s.title}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: i * 0.1 }}
            >
              <TiltCard className="glass-card rounded-xl sm:rounded-2xl p-6 sm:p-7 cursor-default h-full" intensity={10}>
                <div className="text-3xl mb-3">{s.icon}</div>
                <div className="font-display text-lg sm:text-xl text-gold">{s.title}</div>
                <div className="hairline my-3 max-w-[60px]" />
                <p className="text-xs sm:text-sm text-muted-foreground leading-relaxed font-light">{s.desc}</p>
              </TiltCard>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}
